import type { ReactNode } from "react";
import { SkyBadge, SkyText } from "@skypointcloud/skypoint-ui";
import { credentialAttentionLabel } from "@shared/credentials/expiry";
import type { OverviewCredentials } from "@shared/types/overview";
import { usePermissions } from "@/hooks/use-permissions";
import { ValidationSuccessIcon } from "@/lib/icons";
import {
  ASK_ADMIN_DESTINATION,
  destinationCredentialsWithheld,
} from "@/lib/transformations/dbtSettings";
import { ROUTES, connectionPath } from "@/lib/routes";
import { OverviewCard, OverviewLink, statusTone } from "./OverviewCard";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Credentials (2026-09-28 cred-check design, "UI"): how many connections and destination
 * sections need attention, each by name with its reason, and "All credentials accepted. Last
 * checked <time>." when none do. The runner checks them once a day (11 PM Pacific by default).
 * Status by colour and a label, never colour alone.
 */
export function CredentialsCard({ credentials }: { credentials: OverviewCredentials }) {
  const zone = useDisplayTz();
  // #1106: the Destination tab is Instance admin and above. Below that the destination's row is
  // its name, not a link to a screen the reader cannot open, and says who acts.
  const { canAny } = usePermissions();
  const canOpenDestination = canAny("instance.binding.read");
  const lastChecked = credentials.lastCheckedAt
    ? `Last checked ${formatInstant(credentials.lastCheckedAt, zone, "short", { named: true })}.`
    : "Not checked yet.";
  // #1306: below Instance admin the server leaves the destination's rows out and says only
  // whether one is on the list, how many need attention and how urgent. The card counts each of
  // them in the heading, as an admin's card counts each destination row, takes the tone an
  // admin's takes, says who to ask, and never gives an all-clear.
  const withheld = credentials.destinationWithheld;
  const destinationWithheld = withheld?.needsAttention === true;
  const withheldCount = destinationWithheld ? Math.max(withheld?.count ?? 1, 1) : 0;
  const needAttention = credentials.needAttention + withheldCount;
  const withheldLine = destinationWithheld ? (
    <SkyText size={300} block testId="overview-credentials-destination-withheld">
      {`${destinationCredentialsWithheld(withheldCount)} ${ASK_ADMIN_DESTINATION}`}
    </SkyText>
  ) : null;
  function renderCredentials(): ReactNode {
    if (!credentials.checksEnabled) {
      return (
        <SkyText size={300} block testId="overview-credentials-off">
          Daily credential checks are off for this deployment.
        </SkyText>
      );
    }
    if (needAttention === 0) {
      return (
        <SkyText size={300} block testId="overview-credentials-ok">
          {credentials.targets === 0 && withheld?.present !== true
            ? "Nothing to check yet. Credentials are checked once a day."
            : `All credentials accepted. ${lastChecked}`}
        </SkyText>
      );
    }
    return (
      <div className="flex flex-col gap-2" data-testid="overview-credentials-attention">
        <SkyText size={300} weight="semibold" block>
          {needAttention === 1
            ? "1 needs attention"
            : `${needAttention.toLocaleString()} need attention`}
        </SkyText>
        {credentials.attention.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {credentials.attention.map((row) => {
              const label = credentialAttentionLabel(
                row.kind === "expiring"
                  ? { kind: "expiring", daysLeft: row.daysLeft ?? 0 }
                  : { kind: row.kind },
                row,
              );
              return (
                <li
                  key={`${row.targetType}:${row.targetId}`}
                  className="flex flex-wrap items-center gap-2"
                >
                  {row.targetType === "dataflow" || canOpenDestination ? (
                    <OverviewLink
                      to={
                        row.targetType === "dataflow"
                          ? connectionPath(row.targetId)
                          : ROUTES.settingsDestination
                      }
                      testId={`overview-credential-${row.targetType}-${row.targetId}`}
                    >
                      {row.name}
                    </OverviewLink>
                  ) : (
                    <SkyText
                      size={300}
                      testId={`overview-credential-${row.targetType}-${row.targetId}`}
                    >
                      {row.name}
                    </SkyText>
                  )}
                  <SkyBadge
                    appearance="tint"
                    color={row.kind === "expiring" ? "warning" : "danger"}
                  >
                    {label}
                  </SkyBadge>
                  {row.targetType !== "dataflow" && !canOpenDestination ? (
                    <SkyText
                      size={200}
                      color="muted"
                      testId={`overview-credential-${row.targetType}-${row.targetId}-ask`}
                    >
                      {ASK_ADMIN_DESTINATION}
                    </SkyText>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
        {withheldLine}
        <SkyText size={200} color="muted" block>
          {lastChecked}
        </SkyText>
      </div>
    );
  }

  return (
    <OverviewCard
      id="credentials"
      title="Credentials"
      icon={ValidationSuccessIcon}
      tone={
        credentials.checksEnabled
          ? statusTone({
              failed:
                credentials.attention.filter((row) => row.kind !== "expiring").length +
                (destinationWithheld && withheld?.kind !== "expiring" ? 1 : 0),
              warning: needAttention,
              ok: credentials.targets + (withheld?.present === true ? 1 : 0),
            })
          : "info"
      }
      testId="overview-credentials"
      action={<OverviewLink to={ROUTES.connections}>Open Connections</OverviewLink>}
    >
      {renderCredentials()}
    </OverviewCard>
  );
}
